import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const emptyAssessmentType = { id: null, name: "", code: "", weight: 0, description: "", is_active: true };
const emptyGrade = { id: null, grade: "", min_score: 0, max_score: 100, remark: "", is_pass: true, is_active: true };
const emptyDay = { id: null, name: "", short_name: "", order: 1, is_active: true };
const emptyPeriod = { id: null, name: "", start_time: "", end_time: "", order: 1, is_break: false, is_active: true };
const emptySlot = { id: null, class_group: "", day: "", period: "", class_subject: "", teacher: "", room: "", notes: "", is_active: true };

function CoreSettings({ defaultTab = "assessment" }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [assessmentTypes, setAssessmentTypes] = useState([]);
  const [gradingScales, setGradingScales] = useState([]);
  const [days, setDays] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [slots, setSlots] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [security, setSecurity] = useState({});
  const [notifications, setNotifications] = useState({});
  const [assessmentForm, setAssessmentForm] = useState(emptyAssessmentType);
  const [gradeForm, setGradeForm] = useState(emptyGrade);
  const [dayForm, setDayForm] = useState(emptyDay);
  const [periodForm, setPeriodForm] = useState(emptyPeriod);
  const [slotForm, setSlotForm] = useState(emptySlot);
  const [slotClassFilter, setSlotClassFilter] = useState("");

  const loadCoreSettings = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("settings/core/bootstrap/");
      const [
        typesRes,
        gradesRes,
        daysRes,
        periodsRes,
        slotsRes,
        groupsRes,
        classSubjectsRes,
        teachersRes,
        securityRes,
        notificationsRes,
      ] = await Promise.all([
        axiosInstance.get("settings/assessment-types/"),
        axiosInstance.get("settings/grading-scales/"),
        axiosInstance.get("settings/timetable-days/"),
        axiosInstance.get("settings/timetable-periods/"),
        axiosInstance.get("settings/timetable-slots/"),
        axiosInstance.get("academic/class-groups/"),
        axiosInstance.get("academic/class-subjects/"),
        axiosInstance.get("academic/setup/teachers/"),
        axiosInstance.get("settings/security/"),
        axiosInstance.get("settings/notifications/"),
      ]);
      setAssessmentTypes(typesRes.data || []);
      setGradingScales(gradesRes.data || []);
      setDays(daysRes.data || []);
      setPeriods(periodsRes.data || []);
      setSlots(slotsRes.data || []);
      setClassGroups(groupsRes.data || []);
      setClassSubjects(classSubjectsRes.data || []);
      setTeachers(teachersRes.data || []);
      setSecurity(securityRes.data || {});
      setNotifications(notificationsRes.data || {});
    } catch (error) {
      console.error(error);
      toast.error("Failed to load core settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => setActiveTab(defaultTab), [defaultTab]);
  useEffect(() => { loadCoreSettings(); }, []);

  const filteredClassSubjects = useMemo(() => {
    if (!slotForm.class_group) return classSubjects;
    return classSubjects.filter((item) => String(item.class_group) === String(slotForm.class_group));
  }, [classSubjects, slotForm.class_group]);

  const visibleSlots = useMemo(() => {
    if (!slotClassFilter) return slots;
    return slots.filter((item) => String(item.class_group) === String(slotClassFilter));
  }, [slots, slotClassFilter]);

  const saveRecord = async (event, form, emptyForm, setForm, url, label) => {
    event.preventDefault();
    try {
      setLoading(true);
      if (form.id) {
        await axiosInstance.patch(`${url}${form.id}/`, form);
        toast.success(`${label} updated.`);
      } else {
        await axiosInstance.post(url, form);
        toast.success(`${label} added.`);
      }
      setForm(emptyForm);
      await loadCoreSettings();
    } catch (error) {
      const data = error.response?.data;
      toast.error(data?.detail || Object.values(data || {})?.flat?.()?.[0] || `Failed to save ${label}.`);
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadCoreSettings();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  const saveSingleton = async (event, url, payload, label, setter) => {
    event.preventDefault();
    try {
      setLoading(true);
      const response = await axiosInstance.put(url, payload);
      setter(response.data || {});
      toast.success(`${label} saved.`);
    } catch (error) {
      toast.error(`Failed to save ${label}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">Core Settings</h5>
              <p className="text-muted mb-0">Configure assessment, timetable slots, security, and notifications.</p>
            </div>
            <button className="btn btn-outline-success" onClick={loadCoreSettings} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {[
          ["assessment", "Assessment Setup", "bi-clipboard-check"],
          ["timetable", "Timetable Setup", "bi-calendar-week"],
          ["security", "Security", "bi-shield-lock"],
          ["notifications", "Notifications", "bi-bell"],
        ].map(([key, label, icon]) => (
          <button key={key} className={`btn btn-sm ${activeTab === key ? "btn-success" : "btn-light border"}`} onClick={() => setActiveTab(key)}>
            <i className={`bi ${icon} me-2`}></i>{label}
          </button>
        ))}
      </div>

      {activeTab === "assessment" && (
        <div className="row g-4">
          <div className="col-xl-6">
            <SetupCard title="Assessment Types">
              <form className="row g-3 mb-4" onSubmit={(e) => saveRecord(e, assessmentForm, emptyAssessmentType, setAssessmentForm, "settings/assessment-types/", "Assessment type")}>
                <TextField label="Name" value={assessmentForm.name} onChange={(v) => setAssessmentForm((p) => ({ ...p, name: v }))} required />
                <TextField label="Code" value={assessmentForm.code} onChange={(v) => setAssessmentForm((p) => ({ ...p, code: v.toUpperCase() }))} required />
                <NumberField label="Weight %" value={assessmentForm.weight} onChange={(v) => setAssessmentForm((p) => ({ ...p, weight: v }))} />
                <TextField label="Description" value={assessmentForm.description || ""} onChange={(v) => setAssessmentForm((p) => ({ ...p, description: v }))} />
                <Switch label="Active" checked={assessmentForm.is_active} onChange={(v) => setAssessmentForm((p) => ({ ...p, is_active: v }))} />
                <SaveBar loading={loading} label={assessmentForm.id ? "Update Type" : "Add Type"} onCancel={assessmentForm.id ? () => setAssessmentForm(emptyAssessmentType) : null} />
              </form>
              <SimpleTable headers={["Code", "Name", "Weight", "Status", "Actions"]} rows={assessmentTypes.map((item) => [item.code, item.name, `${item.weight}%`, <Status value={item.is_active} />, <ActionButtons onEdit={() => setAssessmentForm(item)} onDelete={() => deleteRecord(`settings/assessment-types/${item.id}/`, item.name)} />])} />
            </SetupCard>
          </div>
          <div className="col-xl-6">
            <SetupCard title="Grading Scale">
              <form className="row g-3 mb-4" onSubmit={(e) => saveRecord(e, gradeForm, emptyGrade, setGradeForm, "settings/grading-scales/", "Grade")}>
                <TextField label="Grade" value={gradeForm.grade} onChange={(v) => setGradeForm((p) => ({ ...p, grade: v.toUpperCase() }))} required />
                <NumberField label="Min Score" value={gradeForm.min_score} onChange={(v) => setGradeForm((p) => ({ ...p, min_score: v }))} />
                <NumberField label="Max Score" value={gradeForm.max_score} onChange={(v) => setGradeForm((p) => ({ ...p, max_score: v }))} />
                <TextField label="Remark" value={gradeForm.remark || ""} onChange={(v) => setGradeForm((p) => ({ ...p, remark: v }))} />
                <Switch label="Pass Grade" checked={gradeForm.is_pass} onChange={(v) => setGradeForm((p) => ({ ...p, is_pass: v }))} />
                <Switch label="Active" checked={gradeForm.is_active} onChange={(v) => setGradeForm((p) => ({ ...p, is_active: v }))} />
                <SaveBar loading={loading} label={gradeForm.id ? "Update Grade" : "Add Grade"} onCancel={gradeForm.id ? () => setGradeForm(emptyGrade) : null} />
              </form>
              <SimpleTable headers={["Grade", "Range", "Remark", "Pass", "Actions"]} rows={gradingScales.map((item) => [item.grade, `${item.min_score} - ${item.max_score}`, item.remark || "-", item.is_pass ? "Yes" : "No", <ActionButtons onEdit={() => setGradeForm(item)} onDelete={() => deleteRecord(`settings/grading-scales/${item.id}/`, item.grade)} />])} />
            </SetupCard>
          </div>
        </div>
      )}

      {activeTab === "timetable" && (
        <div className="row g-4">
          <div className="col-xl-5">
            <SetupCard title="Days & Periods">
              <form className="row g-3 mb-4" onSubmit={(e) => saveRecord(e, dayForm, emptyDay, setDayForm, "settings/timetable-days/", "Timetable day")}>
                <TextField label="Day Name" value={dayForm.name} onChange={(v) => setDayForm((p) => ({ ...p, name: v }))} required />
                <TextField label="Short Name" value={dayForm.short_name || ""} onChange={(v) => setDayForm((p) => ({ ...p, short_name: v }))} />
                <NumberField label="Order" value={dayForm.order} onChange={(v) => setDayForm((p) => ({ ...p, order: v }))} />
                <Switch label="Active" checked={dayForm.is_active} onChange={(v) => setDayForm((p) => ({ ...p, is_active: v }))} />
                <SaveBar loading={loading} label={dayForm.id ? "Update Day" : "Add Day"} onCancel={dayForm.id ? () => setDayForm(emptyDay) : null} />
              </form>
              <SimpleTable headers={["Day", "Order", "Status", "Actions"]} rows={days.map((item) => [item.name, item.order, <Status value={item.is_active} />, <ActionButtons onEdit={() => setDayForm(item)} onDelete={() => deleteRecord(`settings/timetable-days/${item.id}/`, item.name)} />])} />

              <hr />
              <form className="row g-3 mb-4" onSubmit={(e) => saveRecord(e, periodForm, emptyPeriod, setPeriodForm, "settings/timetable-periods/", "Timetable period")}>
                <TextField label="Period Name" value={periodForm.name} onChange={(v) => setPeriodForm((p) => ({ ...p, name: v }))} required />
                <TimeField label="Start" value={periodForm.start_time} onChange={(v) => setPeriodForm((p) => ({ ...p, start_time: v }))} />
                <TimeField label="End" value={periodForm.end_time} onChange={(v) => setPeriodForm((p) => ({ ...p, end_time: v }))} />
                <NumberField label="Order" value={periodForm.order} onChange={(v) => setPeriodForm((p) => ({ ...p, order: v }))} />
                <Switch label="Break/Lunch" checked={periodForm.is_break} onChange={(v) => setPeriodForm((p) => ({ ...p, is_break: v }))} />
                <Switch label="Active" checked={periodForm.is_active} onChange={(v) => setPeriodForm((p) => ({ ...p, is_active: v }))} />
                <SaveBar loading={loading} label={periodForm.id ? "Update Period" : "Add Period"} onCancel={periodForm.id ? () => setPeriodForm(emptyPeriod) : null} />
              </form>
              <SimpleTable headers={["Period", "Time", "Type", "Actions"]} rows={periods.map((item) => [item.name, `${item.start_time} - ${item.end_time}`, item.is_break ? "Break" : "Lesson", <ActionButtons onEdit={() => setPeriodForm(item)} onDelete={() => deleteRecord(`settings/timetable-periods/${item.id}/`, item.name)} />])} />
            </SetupCard>
          </div>

          <div className="col-xl-7">
            <SetupCard title="Class Timetable Slots">
              <div className="alert alert-light border">
                Each class group has its own timetable. Specialist subjects like ICT, French, Agriculture, Arts, Music and PE are assigned through class-subjects and teacher allocations.
              </div>
              <form className="row g-3 mb-4" onSubmit={(e) => saveRecord(e, slotForm, emptySlot, setSlotForm, "settings/timetable-slots/", "Timetable slot")}>
                <SelectField label="Class Group" value={slotForm.class_group} onChange={(v) => setSlotForm((p) => ({ ...p, class_group: v, class_subject: "" }))} options={classGroups.map((g) => ({ value: g.id, label: `${g.academic_year_name} - ${g.display_name}` }))} />
                <SelectField label="Day" value={slotForm.day} onChange={(v) => setSlotForm((p) => ({ ...p, day: v }))} options={days.map((d) => ({ value: d.id, label: d.name }))} />
                <SelectField label="Period" value={slotForm.period} onChange={(v) => setSlotForm((p) => ({ ...p, period: v }))} options={periods.map((p) => ({ value: p.id, label: `${p.name} (${p.start_time}-${p.end_time})` }))} />
                <SelectField label="Class Subject" value={slotForm.class_subject} onChange={(v) => setSlotForm((p) => ({ ...p, class_subject: v }))} options={filteredClassSubjects.map((cs) => ({ value: cs.id, label: `${cs.class_group_name} - ${cs.subject_name} (${cs.subject_type_label || cs.subject_type})` }))} optional />
                <SelectField label="Teacher" value={slotForm.teacher} onChange={(v) => setSlotForm((p) => ({ ...p, teacher: v }))} options={teachers.map((t) => ({ value: t.id, label: `${t.full_name} (${t.employee_number || "No employee no."})` }))} optional />
                <TextField label="Room" value={slotForm.room || ""} onChange={(v) => setSlotForm((p) => ({ ...p, room: v }))} />
                <TextField label="Notes" value={slotForm.notes || ""} onChange={(v) => setSlotForm((p) => ({ ...p, notes: v }))} />
                <Switch label="Active" checked={slotForm.is_active} onChange={(v) => setSlotForm((p) => ({ ...p, is_active: v }))} />
                <SaveBar loading={loading} label={slotForm.id ? "Update Slot" : "Add Slot"} onCancel={slotForm.id ? () => setSlotForm(emptySlot) : null} />
              </form>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">Timetable Slots</h6>
                <select className="form-select form-select-sm" style={{ maxWidth: 280 }} value={slotClassFilter} onChange={(e) => setSlotClassFilter(e.target.value)}>
                  <option value="">All class groups</option>
                  {classGroups.map((g) => <option key={g.id} value={g.id}>{g.academic_year_name} - {g.display_name}</option>)}
                </select>
              </div>
              <SimpleTable
                headers={["Class", "Day", "Period", "Subject", "Teacher", "Room", "Actions"]}
                rows={visibleSlots.map((item) => [
                  item.class_group_name,
                  item.day_name,
                  `${item.period_name} (${item.period_time})`,
                  item.subject_name || "Break/Lunch",
                  item.teacher_name || "-",
                  item.room || "-",
                  <ActionButtons onEdit={() => setSlotForm(normalizeSlot(item))} onDelete={() => deleteRecord(`settings/timetable-slots/${item.id}/`, item.period_name)} />,
                ])}
              />
            </SetupCard>
          </div>
        </div>
      )}

      {activeTab === "security" && (
        <SetupCard title="Security Settings">
          <form onSubmit={(e) => saveSingleton(e, "settings/security/", security, "security settings", setSecurity)}>
            <div className="row g-3">
              <NumberInput label="Minimum Password Length" value={security.minimum_password_length || 8} onChange={(v) => setSecurity((p) => ({ ...p, minimum_password_length: v }))} />
              <NumberInput label="Session Timeout Minutes" value={security.session_timeout_minutes || 60} onChange={(v) => setSecurity((p) => ({ ...p, session_timeout_minutes: v }))} />
              <NumberInput label="Max Failed Login Attempts" value={security.max_failed_login_attempts || 5} onChange={(v) => setSecurity((p) => ({ ...p, max_failed_login_attempts: v }))} />
              <NumberInput label="Lockout Minutes" value={security.lockout_minutes || 15} onChange={(v) => setSecurity((p) => ({ ...p, lockout_minutes: v }))} />
              <Switch label="Require password change on first login" checked={Boolean(security.require_password_change_on_first_login)} onChange={(v) => setSecurity((p) => ({ ...p, require_password_change_on_first_login: v }))} />
              <Switch label="Require transaction PIN for finance actions" checked={Boolean(security.require_transaction_pin_for_finance)} onChange={(v) => setSecurity((p) => ({ ...p, require_transaction_pin_for_finance: v }))} />
            </div>
            <SaveBar loading={loading} label="Save Security Settings" />
          </form>
        </SetupCard>
      )}

      {activeTab === "notifications" && (
        <SetupCard title="Notification Settings">
          <form onSubmit={(e) => saveSingleton(e, "settings/notifications/", notifications, "notification settings", setNotifications)}>
            <div className="row g-3">
              <Switch label="Enable email notifications" checked={Boolean(notifications.enable_email_notifications)} onChange={(v) => setNotifications((p) => ({ ...p, enable_email_notifications: v }))} />
              <Switch label="Enable system notifications" checked={Boolean(notifications.enable_system_notifications)} onChange={(v) => setNotifications((p) => ({ ...p, enable_system_notifications: v }))} />
              <Switch label="Notify parent on payment" checked={Boolean(notifications.notify_parent_on_payment)} onChange={(v) => setNotifications((p) => ({ ...p, notify_parent_on_payment: v }))} />
              <Switch label="Notify parent when results are published" checked={Boolean(notifications.notify_parent_on_results_publish)} onChange={(v) => setNotifications((p) => ({ ...p, notify_parent_on_results_publish: v }))} />
              <Switch label="Notify teacher when allocated" checked={Boolean(notifications.notify_teacher_on_allocation)} onChange={(v) => setNotifications((p) => ({ ...p, notify_teacher_on_allocation: v }))} />
              <Switch label="Notify admin on parent claim" checked={Boolean(notifications.notify_admin_on_parent_claim)} onChange={(v) => setNotifications((p) => ({ ...p, notify_admin_on_parent_claim: v }))} />
            </div>
            <SaveBar loading={loading} label="Save Notification Settings" />
          </form>
        </SetupCard>
      )}
    </div>
  );
}

function normalizeSlot(item) {
  return {
    id: item.id,
    class_group: item.class_group || "",
    day: item.day || "",
    period: item.period || "",
    class_subject: item.class_subject || "",
    teacher: item.teacher || "",
    room: item.room || "",
    notes: item.notes || "",
    is_active: Boolean(item.is_active),
  };
}

function SetupCard({ title, children }) {
  return <div className="card border-0 shadow-sm"><div className="card-header bg-white"><h6 className="fw-bold mb-0">{title}</h6></div><div className="card-body">{children}</div></div>;
}
function TextField({ label, value, onChange, required = false }) {
  return <div className="col-md-4"><label className="form-label fw-semibold">{label}</label><input className="form-control" value={value} onChange={(e) => onChange(e.target.value)} required={required} /></div>;
}
function NumberField({ label, value, onChange }) {
  return <div className="col-md-3"><label className="form-label fw-semibold">{label}</label><input type="number" step="0.01" min="0" className="form-control" value={value} onChange={(e) => onChange(e.target.value)} /></div>;
}
function NumberInput({ label, value, onChange }) {
  return <div className="col-md-3"><label className="form-label fw-semibold">{label}</label><input type="number" min="1" className="form-control" value={value} onChange={(e) => onChange(e.target.value)} /></div>;
}
function TimeField({ label, value, onChange }) {
  return <div className="col-md-3"><label className="form-label fw-semibold">{label}</label><input type="time" className="form-control" value={value} onChange={(e) => onChange(e.target.value)} required /></div>;
}
function SelectField({ label, value, onChange, options, optional = false }) {
  return <div className="col-md-4"><label className="form-label fw-semibold">{label}</label><select className="form-select" value={value || ""} onChange={(e) => onChange(e.target.value)} required={!optional}><option value="">Select {label.toLowerCase()}</option>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>;
}
function Switch({ label, checked, onChange }) {
  return <div className="col-md-6"><label className="form-check form-switch border rounded-3 p-3 h-100"><input type="checkbox" className="form-check-input ms-0 me-2" checked={checked} onChange={(e) => onChange(e.target.checked)} /><span className="form-check-label fw-semibold">{label}</span></label></div>;
}
function SaveBar({ loading, label, onCancel }) {
  return <div className="col-12 d-flex flex-wrap gap-2 mt-3"><button className="btn btn-success fw-semibold" disabled={loading}>{loading ? "Saving..." : label}</button>{onCancel && <button type="button" className="btn btn-light border fw-semibold" onClick={onCancel}>Cancel Edit</button>}</div>;
}
function Status({ value }) {
  return <span className={`badge ${value ? "bg-success" : "bg-secondary"}`}>{value ? "Active" : "Inactive"}</span>;
}
function ActionButtons({ onEdit, onDelete }) {
  return <div className="d-flex gap-1"><button type="button" className="btn btn-sm btn-light border" onClick={onEdit} title="Edit"><i className="bi bi-pencil-square text-success"></i></button><button type="button" className="btn btn-sm btn-light border" onClick={onDelete} title="Delete"><i className="bi bi-trash text-danger"></i></button></div>;
}
function SimpleTable({ headers, rows }) {
  return <div className="table-responsive"><table className="table table-hover align-middle mb-0"><thead className="table-light"><tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr></thead><tbody>{rows.length ? rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>) : <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records yet.</td></tr>}</tbody></table></div>;
}

export default CoreSettings;
